import React, { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Chip,
	Grid,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Typography,
} from "@mui/material";
import ArticleIcon from "@mui/icons-material/Article";
import LaunchIcon from "@mui/icons-material/Launch";
import DescriptionIcon from "@mui/icons-material/Description";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import { useTranslation } from "react-i18next";
import { ASSETS_BASE_URL } from "../../config";

const EulaLibraryModal = ({ open, setOpen, documents = [] }) => {
	const { t } = useTranslation();
	const [selectedId, setSelectedId] = useState(null);

	useEffect(() => {
		if (open) {
			setSelectedId(documents[0]?.id || null);
		}
	}, [open, documents]);

	const selectedDoc = useMemo(
		() => documents.find((doc) => doc.id === selectedId) || null,
		[documents, selectedId]
	);

	const handleOpenExternal = (url) => {
		if (url) window.open(url, "_blank", "noopener,noreferrer");
	};

	const handleOpenFile = (path) => {
		if (path) {
			window.open(`${ASSETS_BASE_URL}/${path}`, "_blank", "noopener,noreferrer");
		}
	};

	const renderDetails = () => {
		if (!selectedDoc) {
			return (
				<Typography variant="body2" color="text.secondary">
					{t("UI_TEXT.NO_EULA_SELECTED") || "Select an EULA to view details."}
				</Typography>
			);
		}

		return (
			<Box display="flex" flexDirection="column" gap={1.5}>
				<Box display="flex" alignItems="center" gap={1}>
					<Typography variant="h6">{selectedDoc.title}</Typography>
					{selectedDoc.version_label && (
						<Chip
							label={`${t("LABELS.VERSION")}: ${selectedDoc.version_label}`}
							size="small"
							color="primary"
						/>
					)}
				</Box>
				{selectedDoc.description_html ? (
					<Box
						sx={{ "& p": { mb: 1 }, maxHeight: 300, overflowY: "auto" }}
						dangerouslySetInnerHTML={{ __html: selectedDoc.description_html }}
					/>
				) : (
					<Typography variant="body2" color="text.secondary">
						{selectedDoc.description_text || t("UI_TEXT.NO_DESCRIPTION_AVAILABLE")}
					</Typography>
				)}
				<Box display="flex" gap={1}>
					{selectedDoc.external_url && (
						<Button
							variant="outlined"
							size="small"
							startIcon={<LaunchIcon />}
							onClick={() => handleOpenExternal(selectedDoc.external_url)}
						>
							{t("BUTTON.OPEN_LINK")}
						</Button>
					)}
					{selectedDoc.file_path && (
						<Button
							variant="outlined"
							size="small"
							startIcon={<DescriptionIcon />}
							onClick={() => handleOpenFile(selectedDoc.file_path)}
						>
							{t("BUTTON.VIEW_FILE")}
						</Button>
					)}
				</Box>
			</Box>
		);
	};

	return (
		<ModalWrapper
			open={open}
			setOpen={setOpen}
			title={t("TITLES.END_USER_LICENSE_AGREEMENT")}
			subtitle={t("UI_TEXT.SELECT_EULA_TO_VIEW") || "Select an EULA to preview the details."}
			width="70vw"
			content={
				<Grid container spacing={2}>
					<Grid item xs={12} md={4}>
						<List dense sx={{ maxHeight: 350, overflowY: "auto", borderRight: (theme) => `1px solid ${theme.palette.divider}` }}>
							{documents.map((doc) => (
								<ListItem disablePadding key={doc.id}>
									<ListItemButton
										selected={doc.id === selectedId}
										onClick={() => setSelectedId(doc.id)}
									>
										<ArticleIcon fontSize="small" style={{ marginRight: 8 }} />
										<ListItemText
											primary={doc.title}
											secondary={
												doc.version_label
													? `${t("LABELS.VERSION")}: ${doc.version_label}`
													: doc.doc_type
											}
										/>
									</ListItemButton>
								</ListItem>
							))}
							{!documents.length && (
								<Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
									{t("UI_TEXT.NO_EULA_AVAILABLE") || "No active EULAs configured."}
								</Typography>
							)}
						</List>
					</Grid>
					<Grid item xs={12} md={8}>
						{renderDetails()}
					</Grid>
				</Grid>
			}
			actions={
				<Button variant="contained" onClick={() => setOpen(false)}>
					{t("BUTTON.CLOSE")}
				</Button>
			}
		/>
	);
};

export default EulaLibraryModal;
